import { isBoolean, isFunction } from "@ciaopeople/cp-shatsu/TypeChecking";
import { VideoInfoRawData } from "../../../model/video/VideoInfo.js";
import { AdUnit } from "../AdUnit.js";
import { GDPRSdk } from "../GDPR.js";
import { AdCallParams } from "../AdCallParams.js";
import { AbstractPrebidModule, AdParamsEnricher } from "./PrebidModule.js";
import { UserAdsData } from "../adcall/composable/UserAdsData.js";



const APSTAG_JS_URL = "https://c.amazon-adsystem.com/aax2/apstag.js";
const INVALID_BID = "_fp_invalid";
const AMZ_NO_BID = "nobid";
const BID_SLOT_NAME = "video";


type AdServer = "googletag"
type VideoAdServer = "DFP"


export interface APSTagConfig {

    pubID: string,
    adServer: AdServer,
    videoAdServer: VideoAdServer,
    bidTimeout: number,
    deals?: boolean

}


export interface RPAData {
    hashedRecords: [{ type: string, record: string }],
    optOut: boolean
}


export interface APSTagBidData {
    mediaType?: string,
    encodedQsParams?: string
    helpers?: {
        encodedQsParams?: () => string
    }
}

export interface APSTagSlot {
    slotID: string,
    slotName?: string,
    sizes?: [number, number][]
    mediaType?: string
}


export interface APSTagSdk {

    init(cfg: APSTagConfig,callback:(() => void)): void;
    rpa(data: RPAData): void;
    fetchBids(sdata: { slots: APSTagSlot[] }, callback: (bids: APSTagBidData[]) => void): void;

}


interface WindowWithApstag {
    apstag: APSTagSdk;
}


function isWindowWithApstag(a: any): a is WindowWithApstag {

    return ("apstag" in a &&
        ("init" in a.apstag && isFunction(a.apstag.init)));

}



function fixStringForPreSerialized(bid_string: string): string {
    let decoded = decodeURIComponent(bid_string);
    if (decoded.substring(0, 1) == "&") decoded = decoded.substring(1);
    return encodeURIComponent(decoded);

}


interface ExternalBidVariables {

    _fp_amz_scp?: string,
    _fp_amz_enabled: boolean,
    _fp_amz_page_load?: boolean
}


function isExternalBidVariables(x: any): x is ExternalBidVariables {
    return (("_fp_amz_enabled" in x) && isBoolean(x._fp_amz_enabled)) &&
        x._fp_amz_enabled;
}

function getExternalBid(w: Window, timeout: number): Promise<string> {
    if (isExternalBidVariables(w)) {
        const cleanAndGet = () => {
            const flag = w._fp_amz_scp;
            w._fp_amz_scp = null;
            w._fp_amz_page_load = false;
            return flag;
        }
        if (w._fp_amz_page_load) {
            const startTime = new Date().getTime();
            return new Promise((resolve, reject) => {

                const rec = () => {

                    if (w._fp_amz_scp && w._fp_amz_scp.trim().length > 0) {
                        if (w._fp_amz_scp != INVALID_BID) resolve(cleanAndGet());
                        else reject("invalid external bid");
                    }
                    else {
                        if (new Date().getTime() - startTime >= timeout) {
                            return reject("external bid search timeout");
                        }
                        else w.setTimeout(rec, 50);
                    }
                }
                rec();
            })
        }
        else {
            if (w._fp_amz_scp && w._fp_amz_scp.trim().length > 0 && w._fp_amz_scp != INVALID_BID) {
                return Promise.resolve(cleanAndGet());
            }
            else {
                return null;
            }
        }
    }
    else return null;
}



export enum AmazonBidBehaviour {


    CHECK_FOR_EXPORTED_BIDS = "check_for_exported",
    ALLOW_PRELOADING_NEXT_BID = "allow_preloading",
    ONLY_ON_DEMAND = "only_on_demand"


}


export class AmazonHB extends AbstractPrebidModule<APSTagSdk> {


    private preloadedBid: Promise<string>;



    constructor(private timeout: number,
        private publisherId: string,
        private userData: UserAdsData,
        private window: Window,
        private bidBehaviour: AmazonBidBehaviour,
        private gdpr: Promise<GDPRSdk>) {
        super(timeout);
    }

    protected internalLoadOps(): Promise<APSTagSdk> {
    
        const config: APSTagConfig = {
            pubID: this.publisherId,
            adServer: "googletag",
            videoAdServer: "DFP",
            bidTimeout: this.timeout,
            deals: true
        }
        if (isWindowWithApstag(this.window)) {
            if (!window['apstaginit']) window['apstag'].init(config,() => {window['apstaginit'] = true; })
            return Promise.resolve(this.window.apstag);
        }
        else {

            (function (a9, a, p, s, t, A, g) {
                if (a[a9]) return;

                function q(c, r) {
                    a[a9]._Q.push([c, r])
                }
                a[a9] = {
                    init: (...args) => {
                        q("i", args)
                    },
                    fetchBids: (...args) => {
                        q("f", args)
                    },
                    setDisplayBids: () => { },
                    targetingKeys: () => {
                        return []
                    },
                    dpa: (...args) => {
                        q("di", args)
                    },
                    rpa: (...args) => {
                        q("ri", args)
                    },
                    upa: (...args) => {
                        q("ui", args)
                    },
                    _Q: []
                };
            })("apstag", this.window);
            return this.gdpr.then(gsdk => {
                return gsdk.amazon.allowPersonalizedAds() && gsdk.amazon.allowHeaderBidding()
            }).then(amazonEnabled => {
                if (amazonEnabled) {
                    const apstag: APSTagSdk = this.window["apstag"];
                    apstag.init(config,() => {
                        window['apstaginit'] = true;
                    });
                    if (!this.userData.isOptOut()) {
                        const apat = this.userData.getApat();
                        if (apat) {
                            apstag.rpa({
                                hashedRecords: [{ type: "email", record: apat }],
                                optOut: false
                            })
                        }
                    }

                    let script: HTMLScriptElement = this.window.document.head.querySelector("script[src='" + APSTAG_JS_URL + "']");

                    if (!script) {
                        script = this.window.document.createElement("script");
                        script.src = APSTAG_JS_URL;
                        script.async = true;
                        this.window.document.head.appendChild(script);
                    }
                    return Promise.resolve(apstag);
                }
                else return Promise.reject("APSTAG NOT ENABLED");

            })

        }
    }

    public get providerName(): string {
        return "A9";
    }



    public preloadNextBid(): void {
        if (this.bidBehaviour == AmazonBidBehaviour.ALLOW_PRELOADING_NEXT_BID) {
            if (!this.preloadedBid) {
                console.info("HB-" + this.providerName, "preloading next bid");
                this.preloadedBid = this.internalLoadOps().then(sdk => this.fetchBidPromise(sdk));
            }
        }
        else {
            console.info("HB-" + this.providerName, "preloading not allowed - discarding request");
        }
    }


    private consumePreloadedBid(): Promise<string> {
        const x = this.preloadedBid;
        this.preloadedBid = null;
        return x;
    }



    protected fetchBidPromise(sdk: APSTagSdk): Promise<string> {
        return new Promise((resolve, reject) => {
            sdk.fetchBids({
                slots: [{
                    slotID: BID_SLOT_NAME,
                    mediaType: "video"
                }]
            }, (bids) => {
                let encodedQS = null;
                if (bids) {
                    const bid = bids.find(bid => {
                        return bid.mediaType == "video"
                    });
                    if (bid) {
                        if (bid.encodedQsParams) {
                            encodedQS = bid.encodedQsParams;
                        }
                        else if (bid.helpers?.encodedQsParams) {
                            encodedQS = bid.helpers.encodedQsParams();
                        }
                    }

                }

                if (encodedQS) resolve(encodedQS);
                else {
                    console.info("HB-" + this.providerName, "no response on fetched bid");
                    resolve(null);
                }
            })
        })
    }

    protected fallbackAdParamsEnricher: AdParamsEnricher = (params: AdCallParams) => {
        const bid = `amznbid=${AMZ_NO_BID}`;
        const useParams: AdCallParams = params ? params : {};
        useParams.custom_params = useParams.custom_params ? useParams.custom_params : {};
        useParams.pre_serialized_custom_params = useParams.pre_serialized_custom_params ? useParams.pre_serialized_custom_params : [];
        const fixed = fixStringForPreSerialized(bid.trim());
        useParams.pre_serialized_custom_params.push(fixed);
        return useParams;
    }

    protected internalBidOps(sdk: APSTagSdk, adUnit: AdUnit, info: VideoInfoRawData): Promise<AdParamsEnricher> {

        let bidPromise: Promise<string>;

        if (this.bidBehaviour == AmazonBidBehaviour.CHECK_FOR_EXPORTED_BIDS) {
            console.info("HB-" + this.providerName, "Checking exported bids");
            bidPromise = getExternalBid(this.window, this.timeout);
        }
        else if (this.bidBehaviour == AmazonBidBehaviour.ALLOW_PRELOADING_NEXT_BID) {
            console.info("HB-" + this.providerName, "consuming preloaded bid");
            bidPromise = this.consumePreloadedBid();
        }

        if (!bidPromise) bidPromise = this.fetchBidPromise(sdk);

        return bidPromise.then(bid => {
            return (params: AdCallParams) => {
                if (!bid) {
                    bid = `amznbid=${AMZ_NO_BID}`;
                }
                const useParams: AdCallParams = params ? params : {};
                useParams.custom_params = useParams.custom_params ? useParams.custom_params : {};
                useParams.pre_serialized_custom_params = useParams.pre_serialized_custom_params ? useParams.pre_serialized_custom_params : [];
                const fixed = fixStringForPreSerialized(bid.trim());
                useParams.pre_serialized_custom_params.push(fixed);
                console.log("[CUST_PARAMS] Appended amazon to pre_serialized", useParams);
                return useParams;
            }
        }).catch(e => {
            console.log("HB-" + this.providerName, "missing bid passing nobid", e);
            return this.fallbackAdParamsEnricher
        });
    }



}
